import { InvoiceData, InvoiceDetails, InvoicePayload } from "@/types/invoice";
import {
  LinkedMembers,
  LinkedMembersInvoiceDataResponse,
} from "@/types/linked-members";
import { ApiResponse } from "@/types";

import { apiSlice } from "@/redux/apiSlice";

export const Invoice = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      getTransactionByMemberId: builder.query<InvoiceData, { query: string }>({
        query: ({ query }) => ({
          url: `/pos/invoices${query !== "" ? "?" + query : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      getLinkedMembers: builder.query<
        LinkedMembersInvoiceDataResponse,
        { id: number; query: string }
      >({
        query: ({ id, query }) => ({
          url: `/pos/business/${id}/linked-members${query !== "" ? "?" + query : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      addLinkedMembers: builder.mutation<
        any,
        {
          member_id: number;
          book_date: string;
          membership_plan_id: number;
          contract_id: number;
          invoice_id: number
        }
      >({
        query: (payload) => ({
          url: `/pos/invoice/${payload.invoice_id}/member/${payload.member_id}`,
          method: "POST",
          body: payload,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      swapLinkedMembers: builder.mutation<
        any,
        {
          invoice_id: number;
          member_id: number;
          replacement_member_id: number;
          contract_id: number;
        }
      >({
        query: (payload) => ({
          url: `/member/${payload.member_id}/swap-membership`,
          method: "PUT",
          body: payload,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      createInvoice: builder.mutation<any, InvoicePayload>({
        query: (invoiceData) => ({
          url: `/pos/invoice`,
          method: "POST",
          body: invoiceData,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      updateInvoice: builder.mutation<any, InvoicePayload>({
        query: (invoiceData) => ({
          url: `/pos/invoice/${invoiceData.id}`,
          method: "PUT",
          body: invoiceData,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      updateInvoiceStatus: builder.mutation<any, any>({
        query: (invoiceData) => ({
          url: `/pos/invoice/${invoiceData.invoice_id}`,
          method: "PUT",
          body: invoiceData,
          header: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      getInvoiceById: builder.query<InvoicePayload, { id: number }>({
        query: (invoiceId) => ({
          url: `/pos/invoices/${invoiceId.id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        keepUnusedDataFor: 5,
        providesTags: ["Invoice", "Transaction","SysConfig","Facility"],
      }),
      sendInvoiceByEmail: builder.mutation<ApiResponse, number>({
        query: (invoiceId) => ({
          url: `/pos/invoices/${invoiceId}/send-email`,
          method: "POST",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice","SysConfig","Facility"],
      }),
    };
  },
});

export const {
  useGetTransactionByMemberIdQuery,
  useAddLinkedMembersMutation,
  useSwapLinkedMembersMutation,
  useGetLinkedMembersQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useUpdateInvoiceStatusMutation,
  useGetInvoiceByIdQuery,
  useLazyGetInvoiceByIdQuery,
  useSendInvoiceByEmailMutation,
} = Invoice;
